import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import PlumbingOutlinedIcon from '@mui/icons-material/PlumbingOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Container, Grid, Link, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import { blueprintGrid, brandColors, brandGradient, hazardStripes, headingFont } from '@/app/theme';
import { BuiltBy, BusinessAddress, LoadingState, PhoneLinks, SupportHours } from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { FAQS } from '@/lib/seo/faq';
import { supportEmail } from '@/lib/site';
import { supportMailto } from '@/lib/support';
import { useIndustries } from '@/services/referenceData';
import { Logo } from '@/layouts/AppShell';
import { InstallLink } from '@/features/install/InstallAppCard';

// Slugs match public.industries: open trades link to the filtered Store, the rest show as coming soon.
const TRADES = [
  { slug: 'electrical', name: 'Electrical', nick: 'Sparkies', icon: <ElectricBoltOutlinedIcon /> },
  { slug: 'plumbing', name: 'Plumbing', nick: 'Plumbers', icon: <PlumbingOutlinedIcon /> },
  { slug: 'gasfitting', name: 'Gasfitting', nick: 'Gasfitters', icon: <LocalFireDepartmentOutlinedIcon /> },
  { slug: 'refrigeration-hvac', name: 'Refrigeration & HVAC', nick: 'Fridgies', icon: <AcUnitOutlinedIcon /> },
  { slug: 'construction', name: 'Construction', nick: 'Chippies & builders', icon: <ConstructionOutlinedIcon /> },
  { slug: 'transport', name: 'Transport', nick: 'Drivers & operators', icon: <LocalShippingOutlinedIcon /> },
  { slug: 'health-safety', name: 'Site safety', nick: 'Everyone on site', icon: <HealthAndSafetyOutlinedIcon /> },
];

// Until the industries list loads, assume only Electrical is open (as at launch).
const OPEN_AT_LAUNCH = new Set(['electrical']);

const EXPERT_POINTS = [
  {
    icon: <BadgeOutlinedIcon />,
    title: 'Credentials checked',
    text: "Every expert's trade registration or qualification is checked by TradePrep before they can publish a single question.",
  },
  {
    icon: <ManageSearchOutlinedIcon />,
    title: 'Numbers on show',
    text: 'Registration numbers are shown on every expert profile, so you can look them up on the public register yourself.',
  },
  {
    icon: <RateReviewOutlinedIcon />,
    title: 'Reviews from real learners',
    text: "Only people who've bought or been given a course can review it, so the stars mean something.",
  },
];

const FEATURES = [
  { icon: <PhoneIphoneOutlinedIcon />, title: 'Study on smoko', text: 'Quick-fire questions on your phone. Five minutes at lunch adds up fast.' },
  { icon: <TimerOutlinedIcon />, title: 'Mock exams that feel real', text: 'Timed exams weighted like the real thing, so exam day is just another day.' },
  { icon: <InsightsOutlinedIcon />, title: 'Know your weak spots', text: 'See your scores by topic and go straight to what you need to work on.' },
  { icon: <LightbulbOutlinedIcon />, title: 'Learn from every miss', text: 'Every answer comes with an explanation from the expert who wrote it. Try free samples before you buy.' },
];

const STEPS = [
  { n: '01', title: 'Pick your course', text: 'Grab an expert-written question bank from the Store, or build your own from your course notes.' },
  { n: '02', title: 'Put in the reps', text: 'Practise with instant explanations. Get it wrong, learn why, go again.' },
  { n: '03', title: 'Walk in ready', text: 'Smash a few mock exams, watch your score climb, then go get your ticket.' },
];

const RESULT_TOPICS: [string, number][] = [
  ['Theory', 91],
  ['Regulations', 72],
  ['Testing & fault finding', 64],
  ['Safe work practices', 88],
];

/** A rubber-stamp style mark, like the PASS stamp on the example result. */
function Stamp({ children, color = '#1e7d43', rotate = -8, size = '1.75rem' }: { children: ReactNode; color?: string; rotate?: number; size?: string }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 0.25,
        border: `3px solid ${color}`,
        color,
        fontFamily: headingFont,
        fontWeight: 800,
        fontSize: size,
        letterSpacing: '0.08em',
        transform: `rotate(${rotate}deg)`,
        borderRadius: 1,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
}

function HazardTape({ height = 10 }: { height?: number }) {
  return <Box aria-hidden sx={{ height, background: hazardStripes }} />;
}

export function LandingPage() {
  const { initialised, user } = useAuth();
  const industries = useIndustries();
  const isOpen = (slug: string) => {
    const found = industries.data?.find((i) => i.slug === slug);
    return found ? !found.coming_soon && found.is_active : OPEN_AT_LAUNCH.has(slug);
  };
  if (!initialised) return <LoadingState />;
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
      {/* Hero */}
      <Box
        component="header"
        sx={{
          color: 'common.white',
          backgroundColor: brandColors.navyDark,
          backgroundImage: `${blueprintGrid}, ${brandGradient}`,
          backgroundSize: '32px 32px, 32px 32px, auto',
          pt: 'env(safe-area-inset-top)',
        }}
      >
        <HazardTape height={8} />
        <Container maxWidth="lg" sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Logo inverted />
          <Box sx={{ flex: 1 }} />
          <Button component={RouterLink} to="/store" sx={{ color: 'common.white', display: { xs: 'none', sm: 'inline-flex' } }}>
            Store
          </Button>
          <Button component={RouterLink} to="/login" sx={{ color: 'common.white' }}>
            Sign in
          </Button>
        </Container>

        <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 9 }, pb: { xs: 8, md: 12 } }}>
          <Grid container spacing={{ xs: 6, md: 8 }} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box
                sx={{
                  display: 'inline-block',
                  px: 1.5,
                  py: 0.5,
                  mb: 3,
                  bgcolor: brandColors.orange,
                  color: brandColors.ink,
                  fontFamily: headingFont,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  fontSize: '0.85rem',
                  transform: 'rotate(-1.5deg)',
                }}
              >
                <VerifiedIcon sx={{ fontSize: '1.1rem', verticalAlign: '-3px', mr: 0.75 }} />
                Written by verified industry experts
              </Box>
              <Typography variant="h1" sx={{ color: 'inherit', fontSize: { xs: '3rem', sm: '3.75rem', md: '4.75rem' }, lineHeight: 0.95 }}>
                Smash your
                <br />
                <Box component="span" sx={{ color: brandColors.orangeBright }}>
                  trade exams.
                </Box>
              </Typography>
              <Typography sx={{ mt: 3, maxWidth: 540, fontSize: { xs: '1.1rem', md: '1.25rem' }, color: 'rgba(255,255,255,0.82)' }}>
                Practise real-style questions and timed mock exams written by people who've done the job — and we've checked their credentials. Study on your phone and see exactly what to work on next.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                <Button size="large" variant="contained" color="secondary" endIcon={<ArrowForwardIcon />} component={RouterLink} to="/register">
                  Start free
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  component={RouterLink}
                  to="/store"
                  sx={{ color: 'common.white', borderColor: 'rgba(255,255,255,0.6)', '&:hover': { borderColor: 'common.white', bgcolor: 'rgba(255,255,255,0.08)' } }}
                >
                  Browse question banks
                </Button>
              </Stack>
              <Stack direction="row" spacing={3} sx={{ mt: 3, flexWrap: 'wrap', rowGap: 1, color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem' }}>
                <span>✓ Every expert verified</span>
                <span>✓ Free to start</span>
                <span>✓ Works on any phone</span>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Paper
                aria-label="Example mock exam result"
                sx={{
                  position: 'relative',
                  p: 3,
                  pt: 4,
                  bgcolor: '#ffffff',
                  color: brandColors.ink,
                  border: `3px solid ${brandColors.ink}`,
                  boxShadow: `8px 8px 0 ${brandColors.orange}`,
                  transform: { md: 'rotate(1.5deg)' },
                  overflow: 'visible',
                }}
              >
                {/* Strip of tape holding the card on */}
                <Box aria-hidden sx={{ position: 'absolute', top: -14, left: '50%', width: 110, height: 26, ml: '-55px', background: hazardStripes, opacity: 0.9, transform: 'rotate(-3deg)' }} />
                <Typography variant="overline" sx={{ color: brandColors.muted }}>
                  Mock exam · Electrical theory
                </Typography>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h1" component="p" sx={{ color: 'inherit', fontSize: '4rem' }}>
                    78%
                  </Typography>
                  <Stamp>PASS</Stamp>
                </Stack>
                {RESULT_TOPICS.map(([name, pct]) => (
                  <Box key={name} sx={{ mb: 1.25 }}>
                    <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {name}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {pct}%
                      </Typography>
                    </Stack>
                    <Box sx={{ height: 10, bgcolor: '#E6EAF2', border: `1px solid ${brandColors.ink}` }}>
                      <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: pct < 70 ? brandColors.orange : brandColors.navy }} />
                    </Box>
                  </Box>
                ))}
                <Typography variant="body2" sx={{ mt: 2, color: brandColors.muted }}>
                  Next up: <strong>Testing & fault finding</strong>
                </Typography>
                <Stack direction="row" spacing={0.75} sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #C9D1DF', alignItems: 'center', color: '#1e7d43' }}>
                  <VerifiedIcon fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Questions by a verified industry expert
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
        <HazardTape />
      </Box>

      {/* Verified experts: the main selling point */}
      <Box component="section" aria-labelledby="experts-heading" sx={{ bgcolor: brandColors.orange, color: brandColors.ink, py: { xs: 6, md: 9 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 6 }} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.12em' }}>
                Who writes the questions
              </Typography>
              <Typography id="experts-heading" variant="h2" sx={{ color: 'inherit', fontSize: { xs: '2.25rem', md: '3.25rem' }, lineHeight: 1, mb: 2 }}>
                Real experts.
                <br />
                Actually verified.
              </Typography>
              <Typography sx={{ fontSize: '1.15rem', mb: 3, maxWidth: 460 }}>
                Anyone can put questions online. On TradePrep, every course comes from an industry expert whose credentials we've checked — so you're learning it right the first time.
              </Typography>
              <Paper
                aria-hidden
                sx={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  gap: 1,
                  p: 2,
                  pr: 3,
                  bgcolor: '#ffffff',
                  color: brandColors.ink,
                  border: `3px solid ${brandColors.ink}`,
                  boxShadow: `6px 6px 0 ${brandColors.ink}`,
                }}
              >
                <Stamp rotate={-4} size="1.3rem">
                  <VerifiedIcon /> Verified expert
                </Stamp>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                  Trade registration checked by TradePrep
                </Typography>
                <Typography variant="body2" sx={{ color: brandColors.muted }}>
                  Registration number shown on their profile
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack spacing={2}>
                {EXPERT_POINTS.map((pt) => (
                  <Paper
                    key={pt.title}
                    sx={{ display: 'flex', gap: 2, p: 2.5, bgcolor: '#ffffff', color: brandColors.ink, border: `2px solid ${brandColors.ink}` }}
                  >
                    <Box sx={{ flexShrink: 0, width: 48, height: 48, display: 'grid', placeItems: 'center', bgcolor: brandColors.ink, color: brandColors.orange, borderRadius: 1 }}>
                      {pt.icon}
                    </Box>
                    <Box>
                      <Typography variant="h5" component="h3" sx={{ color: 'inherit' }}>
                        {pt.title}
                      </Typography>
                      <Typography sx={{ color: brandColors.muted }}>{pt.text}</Typography>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <HazardTape height={8} />

      {/* Trades */}
      <Container maxWidth="lg" component="section" sx={{ py: { xs: 6, md: 9 } }}>
        <Typography variant="overline" sx={{ color: 'secondary.main' }}>
          Pick your trade
        </Typography>
        <Typography variant="h2" sx={{ mb: 1, fontSize: { xs: '2rem', md: '2.75rem' } }}>
          Sparkies first. More trades on the way.
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 640 }}>
          We're starting with electrical and bringing on verified experts for each new trade before it opens.
        </Typography>
        <Grid container spacing={2}>
          {TRADES.map((t) => {
            const open = isOpen(t.slug);
            const body = (
              <>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: open ? brandColors.orange : 'action.disabledBackground',
                      color: open ? brandColors.ink : 'text.secondary',
                      borderRadius: 1,
                    }}
                  >
                    {t.icon}
                  </Box>
                  <Chip size="small" label={open ? 'Live now' : 'Coming soon'} color={open ? 'success' : 'default'} variant={open ? 'filled' : 'outlined'} />
                </Stack>
                <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
                  {t.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t.nick}
                </Typography>
                {open && (
                  <Typography variant="body2" sx={{ mt: 'auto', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Browse courses <ArrowForwardIcon fontSize="small" />
                  </Typography>
                )}
              </>
            );
            return (
              <Grid key={t.slug} size={open ? { xs: 12, sm: 8, md: 6 } : { xs: 6, sm: 4, md: 3 }}>
                {open ? (
                  <Paper
                    component={RouterLink}
                    to={`/store/search?industry=${t.slug}`}
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      textDecoration: 'none',
                      color: 'text.primary',
                      borderWidth: 3,
                      borderColor: 'text.primary',
                      boxShadow: `4px 4px 0 ${brandColors.orange}`,
                      transition: 'transform 120ms ease, box-shadow 120ms ease',
                      '&:hover, &:focus-visible': { transform: 'translate(-2px, -2px)', boxShadow: `6px 6px 0 ${brandColors.orange}` },
                    }}
                  >
                    {body}
                  </Paper>
                ) : (
                  <Paper
                    variant="outlined"
                    aria-label={`${t.name}: coming soon`}
                    sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1, borderStyle: 'dashed', borderWidth: 2, opacity: 0.75 }}
                  >
                    {body}
                  </Paper>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* Features */}
      <Box component="section" sx={{ bgcolor: 'background.paper', borderBlock: 1, borderColor: 'divider', py: { xs: 6, md: 9 } }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: 'secondary.main' }}>
            Why TradePrep
          </Typography>
          <Typography variant="h2" sx={{ mb: 4, fontSize: { xs: '2rem', md: '2.75rem' } }}>
            Study smarter, not longer
          </Typography>
          <Grid container spacing={3}>
            {FEATURES.map((f) => (
              <Grid key={f.title} size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ borderTop: 4, borderColor: 'secondary.main', pt: 2 }}>
                  <Box sx={{ color: 'secondary.main', mb: 1, '& svg': { fontSize: 32 } }}>{f.icon}</Box>
                  <Typography variant="h5" sx={{ mb: 0.5 }}>
                    {f.title}
                  </Typography>
                  <Typography color="text.secondary">{f.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How it works */}
      <Container maxWidth="lg" component="section" sx={{ py: { xs: 6, md: 9 } }}>
        <Typography variant="overline" sx={{ color: 'secondary.main' }}>
          How it works
        </Typography>
        <Typography variant="h2" sx={{ mb: 4, fontSize: { xs: '2rem', md: '2.75rem' } }}>
          Three steps to your ticket
        </Typography>
        <Grid container spacing={3}>
          {STEPS.map((s) => (
            <Grid key={s.n} size={{ xs: 12, md: 4 }}>
              <Stack direction="row" spacing={2}>
                <Typography
                  aria-hidden
                  sx={{ fontFamily: headingFont, fontWeight: 800, fontSize: '3.5rem', lineHeight: 0.9, color: 'transparent', WebkitTextStroke: (t) => `2px ${(t.vars ?? t).palette.secondary.main}` }}
                >
                  {s.n}
                </Typography>
                <Box>
                  <Typography variant="h5">{s.title}</Typography>
                  <Typography color="text.secondary">{s.text}</Typography>
                </Box>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Questions people ask (also the page's FAQ structured data, so keep them the same: src/lib/seo/faq.ts) */}
      <Box component="section" aria-labelledby="faq-heading" sx={{ bgcolor: 'background.paper', borderBlock: 1, borderColor: 'divider', py: { xs: 6, md: 9 } }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ color: 'secondary.main' }}>
            Good to know
          </Typography>
          <Typography id="faq-heading" variant="h2" sx={{ mb: 3, fontSize: { xs: '2rem', md: '2.75rem' } }}>
            Questions people ask
          </Typography>
          {FAQS.map((f) => (
            <Accordion key={f.question} disableGutters variant="outlined" slotProps={{ heading: { component: 'h3' } }} sx={{ '&:not(:last-child)': { borderBottom: 0 } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>{f.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{f.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* Final CTA */}
      <Box component="section" sx={{ background: hazardStripes, p: { xs: 1.5, md: 2 } }}>
        <Box sx={{ bgcolor: brandColors.navyDark, color: 'common.white', py: { xs: 6, md: 8 }, px: 2, textAlign: 'center' }}>
          <Typography variant="h2" sx={{ color: 'inherit', fontSize: { xs: '2.25rem', md: '3.25rem' } }}>
            Your ticket's waiting.
          </Typography>
          <Typography sx={{ mt: 1, mb: 4, color: 'rgba(255,255,255,0.8)', fontSize: '1.1rem' }}>Sign up free and smash your first practice set in five minutes.</Typography>
          <Button size="large" variant="contained" color="secondary" endIcon={<ArrowForwardIcon />} component={RouterLink} to="/register">
            Let's go
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" component="footer" sx={{ py: 4, color: 'text.secondary', pb: 'calc(32px + env(safe-area-inset-bottom))' }}>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          <Link component={RouterLink} to="/store" sx={{ fontWeight: 600 }}>
            Browse the Store
          </Link>
          {' · '}
          <Link component={RouterLink} to="/login" sx={{ fontWeight: 600 }}>
            Sign in
          </Link>
          {' · '}
          Questions? Email{' '}
          <Link href={supportMailto()} sx={{ fontWeight: 600 }}>
            {supportEmail()}
          </Link>{' '}
          or call <PhoneLinks />, or visit{' '}
          <Link component={RouterLink} to="/support" sx={{ fontWeight: 600 }}>
            Help &amp; support
          </Link>
          {' · '}
          <Link component={RouterLink} to="/guides" sx={{ fontWeight: 600 }}>
            Study guides
          </Link>
          {' · '}
          <Link component={RouterLink} to="/about" sx={{ fontWeight: 600 }}>
            About
          </Link>
          {' · '}
          <Link component={RouterLink} to="/terms" sx={{ fontWeight: 600 }}>
            Terms of use
          </Link>
          {' · '}
          <Link component={RouterLink} to="/refunds" sx={{ fontWeight: 600 }}>
            Refund policy
          </Link>
          {' · '}
          <Link component={RouterLink} to="/privacy" sx={{ fontWeight: 600 }}>
            Privacy policy
          </Link>
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          <BusinessAddress />. Support: <SupportHours />.
        </Typography>
        <Box sx={{ mb: 1.5, ml: -1 }}>
          <InstallLink />
        </Box>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          <BuiltBy />
        </Typography>
        <Typography variant="body2">
          © {new Date().getFullYear()} TradePrep. TradePrep is an independent study platform and is not affiliated with or endorsed by any regulator, licensing board or examination body unless explicitly stated on a product.
        </Typography>
      </Container>
    </Box>
  );
}

export function NotFoundPage() {
  return (
    <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
      <Typography variant="h2">Page not found</Typography>
      <Typography color="text.secondary" sx={{ my: 2 }}>
        Looks like this one's not on the plans. The page you're after doesn't exist or has moved.
      </Typography>
      <Button variant="contained" component={RouterLink} to="/">
        Go home
      </Button>
    </Container>
  );
}
